import { boundedText } from "./bounded-text.js";
import { DomainValidationError } from "./identifiers.js";
import { localDate, localTimeOfDay } from "./time-zone.js";
// Questions that staff ask a customer while a request is AWAITING_CUSTOMER, and the answers (CHT-751, contract 7.4). Pure rules: no
// clock, no port, no model. The application stores the rows; the agent tool `answerRequestQuestion` calls `answerFromChatMessage`.
//
//   - A question is a prompt (customer text) and 1 to 8 typed fields. A plain text question is ONE `TEXT` field named `reply`.
//   - An answer holds one value per field key, typed by the field. A required field must be there. A value is validated, never repaired.
//   - A CHAT answer is the strict case: the agent may only RECORD what the customer wrote. Every value must be a whole token (or a whole
//     token sequence) of ONE customer message that was sent after the question was asked. A doubt is `CLARIFY`, never a guess.
export const questionFieldTypes = Object.freeze([
    "TEXT",
    "NUMBER",
    "DATE",
    "TIME",
    "CHOICE",
    "BOOLEAN",
]);
export const questionStatuses = Object.freeze(["OPEN", "ANSWERED", "WITHDRAWN"]);
export const questionAnswerChannels = Object.freeze([
    "ACTION_CENTER",
    "CHAT",
    "STAFF_RECORDED",
]);
export const QUESTION_LIMITS = Object.freeze({
    maxFields: 8,
    promptChars: 1_000,
    keyChars: 32,
    labelChars: 100,
    optionChars: 60,
    minOptions: 2,
    maxOptions: 12,
    textAnswerChars: 2_000,
    maxNumber: 1_000_000_000,
    /** The size of the serialized answers. The table CHECK is looser (16 KB), because `jsonb` text is a little longer than `JSON.stringify`. */
    answerBytes: 12_288,
});
/** The field of a plain text question (what `POST .../ask-customer` with no fields, and the free-text `respond`, use). */
export const PLAIN_QUESTION_FIELD_KEY = "reply";
export const PLAIN_QUESTION_FIELDS = Object.freeze([
    Object.freeze({
        key: PLAIN_QUESTION_FIELD_KEY,
        label: "Your reply",
        type: "TEXT",
        required: true,
    }),
]);
/** The neutral line the history row of a structured answer carries (the answers are in the question row, not in the history). */
export const STRUCTURED_ANSWER_HISTORY_TEXT = "Answered the question.";
export function isPlainQuestion(fields) {
    const [only] = fields;
    return (fields.length === 1 &&
        only !== undefined &&
        only.key === PLAIN_QUESTION_FIELD_KEY &&
        only.type === "TEXT" &&
        only.required);
}
const KEY_SHAPE = /^[a-z][a-zA-Z0-9]*$/;
const normalizeKey = (value) => value.normalize("NFKC").toLowerCase();
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function assertKnownKeys(value, allowed, field) {
    for (const key of Object.keys(value)) {
        if (!allowed.includes(key))
            throw new DomainValidationError(`${field}.${key}`, "is not allowed");
    }
}
/**
 * The fields of a question: 1 to 8, each with a camel-case key (distinct), a label, a closed type and `required`. `CHOICE` has 2 to 12
 * distinct options and no other type has any. Returns the canonical frozen list (trimmed text), so the stored `fields` never hold a
 * key that this function would refuse.
 */
export function validateQuestionFields(input) {
    if (!Array.isArray(input))
        throw new DomainValidationError("fields", "must be a list");
    if (input.length < 1 || input.length > QUESTION_LIMITS.maxFields) {
        throw new DomainValidationError("fields", `must hold 1 to ${QUESTION_LIMITS.maxFields} fields`);
    }
    const seen = new Set();
    const out = [];
    input.forEach((raw, index) => {
        const at = `fields[${index}]`;
        if (!isRecord(raw))
            throw new DomainValidationError(at, "must be an object");
        assertKnownKeys(raw, ["key", "label", "type", "required", "options"], at);
        const key = raw.key;
        if (typeof key !== "string" || key.length > QUESTION_LIMITS.keyChars || !KEY_SHAPE.test(key)) {
            throw new DomainValidationError(`${at}.key`, "must be a short camel-case name");
        }
        if (seen.has(normalizeKey(key)))
            throw new DomainValidationError(`${at}.key`, "must be unique");
        seen.add(normalizeKey(key));
        const label = boundedText(`${at}.label`, raw.label, QUESTION_LIMITS.labelChars);
        const type = raw.type;
        if (typeof type !== "string" || !questionFieldTypes.includes(type)) {
            throw new DomainValidationError(`${at}.type`, "must be a known field type");
        }
        if (typeof raw.required !== "boolean") {
            throw new DomainValidationError(`${at}.required`, "must be true or false");
        }
        const fieldType = type;
        if (fieldType !== "CHOICE") {
            if (raw.options !== undefined && raw.options !== null) {
                throw new DomainValidationError(`${at}.options`, "belongs to a CHOICE field only");
            }
            out.push(Object.freeze({ key, label, type: fieldType, required: raw.required }));
            return;
        }
        if (!Array.isArray(raw.options) ||
            raw.options.length < QUESTION_LIMITS.minOptions ||
            raw.options.length > QUESTION_LIMITS.maxOptions) {
            throw new DomainValidationError(`${at}.options`, `must hold ${QUESTION_LIMITS.minOptions} to ${QUESTION_LIMITS.maxOptions} options`);
        }
        const options = [];
        const optionSeen = new Set();
        raw.options.forEach((option, optionIndex) => {
            const text = boundedText(`${at}.options[${optionIndex}]`, option, QUESTION_LIMITS.optionChars);
            const folded = text.normalize("NFKC").toLowerCase();
            if (optionSeen.has(folded)) {
                throw new DomainValidationError(`${at}.options[${optionIndex}]`, "must be distinct");
            }
            optionSeen.add(folded);
            options.push(text);
        });
        out.push(Object.freeze({
            key,
            label,
            type: fieldType,
            required: raw.required,
            options: Object.freeze(options),
        }));
    });
    return Object.freeze(out);
}
/** The prompt of a question: customer-facing text of 1 to 1,000 characters (the column `pending_customer_question` holds the same text). */
export const questionPrompt = (value) => boundedText("prompt", value, QUESTION_LIMITS.promptChars, { multiline: true });
function validateValue(field, value, at) {
    switch (field.type) {
        case "TEXT":
            return boundedText(at, value, QUESTION_LIMITS.textAnswerChars, { multiline: true });
        case "NUMBER":
            if (typeof value !== "number" ||
                !Number.isFinite(value) ||
                Math.abs(value) > QUESTION_LIMITS.maxNumber) {
                throw new DomainValidationError(at, "must be a number");
            }
            return value;
        case "DATE":
            return localDate(typeof value === "string" ? value.trim() : value, at);
        case "TIME": {
            const time = localTimeOfDay(typeof value === "string" ? value.trim() : value, at);
            return time.endsWith(":00") ? time.slice(0, 5) : time;
        }
        case "CHOICE": {
            const options = field.options ?? [];
            if (typeof value !== "string" || !options.includes(value.trim())) {
                throw new DomainValidationError(at, "must be one of the options");
            }
            return value.trim();
        }
        case "BOOLEAN":
            if (typeof value !== "boolean")
                throw new DomainValidationError(at, "must be true or false");
            return value;
    }
}
/**
 * The answers of a question, validated against its fields: an object whose every key is a field, every required field present, every
 * value of the field's type (a date is `YYYY-MM-DD`, a time `HH:MM`, a choice one of its options). Nothing is converted ("2" is not the
 * number 2) and an unknown key is refused. `null` or `undefined` is "no value": refused for a required field, left out for an optional
 * one. Returns the canonical frozen answers in field order.
 */
export function validateQuestionAnswer(fields, input) {
    if (!isRecord(input))
        throw new DomainValidationError("answers", "must be an object");
    const known = fields.map((field) => field.key);
    for (const key of Object.keys(input)) {
        if (!known.includes(key))
            throw new DomainValidationError(`answers.${key}`, "is not asked");
    }
    const out = {};
    for (const field of fields) {
        const value = input[field.key];
        if (value === undefined || value === null) {
            if (field.required)
                throw new DomainValidationError(`answers.${field.key}`, "is required");
            continue;
        }
        out[field.key] = validateValue(field, value, `answers.${field.key}`);
    }
    if (Object.keys(out).length === 0) {
        throw new DomainValidationError("answers", "must answer at least one field");
    }
    if (new TextEncoder().encode(JSON.stringify(out)).length > QUESTION_LIMITS.answerBytes) {
        throw new DomainValidationError("answers", "are too large");
    }
    return Object.freeze(out);
}
// --- The chat rule ---------------------------------------------------------------------------------
export const chatClarifyReasons = Object.freeze([
    "NOT_CUSTOMER_MESSAGE",
    "MESSAGE_TIME_UNKNOWN",
    "BEFORE_QUESTION",
    "UNKNOWN_FIELD",
    "FIELD_MISSING",
    "VALUE_INVALID",
    "NOT_IN_MESSAGE",
    "AMBIGUOUS",
    "RELATIVE_DATE",
    "USE_FORM",
]);
const clarify = (reason, fieldKeys = []) => ({
    outcome: "CLARIFY",
    reason,
    fieldKeys,
});
/** NFKC, lower case, one space between words, a straight apostrophe, and one ASCII minus for the minus sign and the dashes. */
const normalizeChat = (text) => text
    .normalize("NFKC")
    .toLowerCase()
    .replace(/[‘’]/g, "'")
    .replace(/[−–—]/g, "-")
    .replace(/\s+/g, " ")
    .trim();
/**
 * Drops the punctuation around a word. A sign that stands right before a digit stays (`-2`, `+2`): `-2` is not `2`. A trailing
 * punctuation mark goes (`2.`, `2,`) but a sign in the middle stays (`2-3`, `2026-11-03`).
 */
function stripEdges(token) {
    const percent = /\p{N}%[^\p{L}\p{N}]*$/u.test(token) ? "%" : "";
    const trimmed = token.replace(/[^\p{L}\p{N}]+$/u, "");
    const first = trimmed.search(/[\p{L}\p{N}]/u);
    if (first === -1)
        return "";
    const mark = trimmed[first - 1];
    const keepMark = first > 0 &&
        (mark === "+" || mark === "-" || mark === ".") &&
        /^\p{N}/u.test(trimmed.slice(first));
    return trimmed.slice(keepMark ? first - 1 : first) + percent;
}
/** The words of a text: normalized, split on spaces, surrounding punctuation stripped (inner punctuation stays: `2.5`, `12:30`, `2026-11-03`; a sign before a number stays). */
export function chatTokens(text) {
    return normalizeChat(text)
        .split(" ")
        .map(stripEdges)
        .filter((token) => token !== "");
}
/** The index of the first place where `needle` is a run of whole tokens of `haystack`, or -1. */
function findRun(haystack, needle) {
    if (needle.length === 0)
        return -1;
    for (let start = 0; start + needle.length <= haystack.length; start += 1) {
        if (needle.every((token, offset) => haystack[start + offset] === token))
            return start;
    }
    return -1;
}
const countRuns = (haystack, needle) => {
    let count = 0;
    for (let start = 0; start + needle.length <= haystack.length; start += 1) {
        if (needle.every((token, offset) => haystack[start + offset] === token))
            count += 1;
    }
    return count;
};
/**
 * The words that put a value in doubt: a negation, a contrast, a condition, a hedge, a choice between values. Token presence does not
 * keep the meaning ("not 2 guests", "if 2", "2 or 3"), so ONE of these anywhere in the message makes every field except a TEXT field
 * that holds the whole message a `CLARIFY`. Only whole tokens count: `now` and `nothing special` are different words. A word in
 * another language that is not listed (`nein`, `nahi`) matches no value either, so it ends as a `CLARIFY` too.
 */
const DOUBT_WORDS = new Set([
    "no",
    "not",
    "nope",
    "nah",
    "never",
    "none",
    "nothing",
    "nobody",
    "without",
    "neither",
    "nor",
    "cannot",
    "but",
    "except",
    "instead",
    "unless",
    "if",
    "maybe",
    "perhaps",
    "possibly",
    "probably",
    "might",
    "or",
    "either",
]);
/** A contraction of a negation: `don't`, `isn't`, `can't`, and the same without the apostrophe is listed in `NEGATED_PLAIN`. */
const NEGATED_CONTRACTION = /n't$/;
const NEGATED_PLAIN = new Set([
    "dont",
    "doesnt",
    "didnt",
    "wont",
    "cant",
    "isnt",
    "arent",
    "wasnt",
    "werent",
    "wouldnt",
    "shouldnt",
    "couldnt",
    "havent",
    "hasnt",
]);
const isDoubtWord = (token) => DOUBT_WORDS.has(token) || NEGATED_PLAIN.has(token) || NEGATED_CONTRACTION.test(token);
/** A question mark, or a quotation mark of a common kind: the customer asks, or quotes someone, and does not state the value. */
const DOUBT_MARKS = /[?"“”„«»]/u;
/** The indexes of the tokens that are doubt words. */
const doubtIndexes = (tokens) => tokens.flatMap((token, index) => (isDoubtWord(token) ? [index] : []));
const PLAIN_NUMBER = /^[0-9]+(\.[0-9]+)?$/;
/** Digits and the signs and marks that join them: `-2`, `+2`, `.5`, `2-3`, `1,000`, `1/2`, `1⁄2`, `50%`, `9:30`, `03/11/2026`, `٣`. */
const NUMERICISH = /^[+-]?\.?\p{Nd}[\p{Nd}.,/:⁄+-]*%?$/u;
const MONTHS = {
    january: 1,
    jan: 1,
    february: 2,
    feb: 2,
    march: 3,
    mar: 3,
    april: 4,
    apr: 4,
    may: 5,
    june: 6,
    jun: 6,
    july: 7,
    jul: 7,
    august: 8,
    aug: 8,
    september: 9,
    sep: 9,
    sept: 9,
    october: 10,
    oct: 10,
    november: 11,
    nov: 11,
    december: 12,
    dec: 12,
};
const RELATIVE_WORDS = new Set([
    "today",
    "tonight",
    "tomorrow",
    "yesterday",
    "next",
    "week",
    "weekend",
    "month",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
    "mon",
    "tue",
    "tues",
    "wed",
    "thu",
    "thur",
    "thurs",
    "fri",
    "sat",
    "sun",
]);
const pad = (n) => String(n).padStart(2, "0");
function realDate(year, month, day) {
    const text = `${String(year).padStart(4, "0")}-${pad(month)}-${pad(day)}`;
    try {
        return localDate(text, "date");
    }
    catch {
        return null;
    }
}
const YEAR = /^\d{4}$/;
const DAY = /^(\d{1,2})(?:st|nd|rd|th)?$/;
/**
 * The explicit dates of a message with their place: `YYYY-MM-DD`, `3 November 2026` and `November 3 2026` (a month name in full or three
 * letters, an ordinal suffix allowed). A date with no year, a numeric `03/11/2026` (day first or month first?) and a relative word are NOT
 * here: they need the clock or a guess.
 */
function dateSpansIn(tokens) {
    const found = [];
    for (let i = 0; i < tokens.length; i += 1) {
        const token = tokens[i];
        const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(token);
        if (iso !== null) {
            const date = realDate(Number(iso[1]), Number(iso[2]), Number(iso[3]));
            if (date !== null)
                found.push({ value: date, start: i, end: i });
            continue;
        }
        const dayFirst = DAY.exec(token);
        const next = tokens[i + 1];
        const afterNext = tokens[i + 2];
        if (dayFirst !== null && next !== undefined && MONTHS[next] !== undefined) {
            // "3 november 2026" and "3 of november 2026" is not read: only the direct form.
            if (afterNext !== undefined && YEAR.test(afterNext)) {
                const date = realDate(Number(afterNext), MONTHS[next], Number(dayFirst[1]));
                if (date !== null)
                    found.push({ value: date, start: i, end: i + 2 });
            }
            continue;
        }
        if (MONTHS[token] !== undefined && next !== undefined && afterNext !== undefined) {
            const monthFirst = DAY.exec(next);
            if (monthFirst !== null && YEAR.test(afterNext)) {
                const date = realDate(Number(afterNext), MONTHS[token], Number(monthFirst[1]));
                if (date !== null)
                    found.push({ value: date, start: i, end: i + 2 });
            }
        }
    }
    return found;
}
export const explicitDatesIn = (tokens) => dateSpansIn(tokens).map((span) => span.value);
const MERIDIEM = {
    am: "am",
    pm: "pm",
    "a.m": "am",
    "p.m": "pm",
};
const ATTACHED_TIME = /^(\d{1,2})(?::([0-5]\d))?(am|pm|a\.m|p\.m)$/;
const CLOCK_TIME = /^(\d{1,2})(?::([0-5]\d))?$/;
/**
 * The explicit times of a message with their place, as `HH:MM`: an hour with `am` or `pm` (`2pm`, `2:30 pm`, `02:30 pm`, `12am`), or a
 * 24-hour time with a two-digit hour (`14:30`, `09:00`). An `am` or `pm` right after a time is read FIRST and takes the whole
 * expression: `02:30 pm` is 14:30 and never 02:30. An hour that cannot have `am` or `pm` (`14:30 pm`, `0 am`, `13pm`) is no time at
 * all. A bare `2:30` is not explicit (02:30 or 14:30?) and neither is `noon`.
 */
function timeSpansIn(tokens) {
    const found = [];
    const meridiem = (hour, minute, suffix) => {
        if (hour < 1 || hour > 12 || minute > 59)
            return null;
        const h24 = suffix === "am" ? (hour === 12 ? 0 : hour) : hour === 12 ? 12 : hour + 12;
        return `${pad(h24)}:${pad(minute)}`;
    };
    for (let i = 0; i < tokens.length; i += 1) {
        const token = tokens[i];
        const attached = ATTACHED_TIME.exec(token);
        if (attached !== null) {
            const time = meridiem(Number(attached[1]), Number(attached[2] ?? "0"), MERIDIEM[attached[3]]);
            if (time !== null)
                found.push({ value: time, start: i, end: i });
            continue;
        }
        const separate = CLOCK_TIME.exec(token);
        const suffix = MERIDIEM[tokens[i + 1] ?? ""];
        if (separate !== null && suffix !== undefined) {
            const time = meridiem(Number(separate[1]), Number(separate[2] ?? "0"), suffix);
            if (time !== null)
                found.push({ value: time, start: i, end: i + 1 });
            i += 1; // the `pm` belongs to this expression, valid or not: it never leaves a bare 24-hour time behind
            continue;
        }
        const twentyFour = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(token);
        if (twentyFour !== null)
            found.push({ value: `${twentyFour[1]}:${twentyFour[2]}`, start: i, end: i });
    }
    return found;
}
export const explicitTimesIn = (tokens) => timeSpansIn(tokens).map((span) => span.value);
/** Words of a label that are no cue for a field ("Number of adults": `adults` is the cue, `number` and `of` are not). */
const CUE_STOP_WORDS = new Set([
    "the",
    "and",
    "for",
    "number",
    "your",
    "how",
    "many",
    "what",
    "are",
    "you",
    "with",
    "date",
    "time",
    "of",
]);
/**
 * The words a customer may write next to a value to name its field: the whole label, the words of the key (`checkOutDate` is
 * `check out date`) and each long label word. A cue that two fields of the group share names neither of them, so it is dropped.
 */
function cueRuns(field, group) {
    const runsOf = (one) => {
        const label = chatTokens(one.label);
        const runs = [label, chatTokens(one.key.replace(/([a-z0-9])([A-Z])/g, "$1 $2"))];
        for (const word of label)
            if (word.length >= 3 && !CUE_STOP_WORDS.has(word))
                runs.push([word]);
        // "1 kid" names the field `kids`: a plural cue is also read in the singular.
        for (const run of [...runs]) {
            const last = run[run.length - 1];
            if (last !== undefined && last.length > 3 && last.endsWith("s"))
                runs.push([...run.slice(0, -1), last.slice(0, -1)]);
        }
        return runs.filter((run) => run.length > 0);
    };
    const others = new Set(group.filter((one) => one !== field).flatMap((one) => runsOf(one).map((run) => run.join(" "))));
    return runsOf(field).filter((run) => !others.has(run.join(" ")));
}
const runAt = (tokens, at, run) => at >= 0 && run.every((token, offset) => tokens[at + offset] === token);
/**
 * Several fields of ONE type (two NUMBER fields `adults` and `children`) cannot be told apart by their values: "4 adults and 2
 * children" holds a 4 and a 2, and the model may swap them. A chat answer is accepted only when each value token sits right before (all
 * values of the message) or right after (all values of the message) the label, key words or a long label word of exactly one field, and
 * each value token serves one field. Else it is a `CLARIFY`: the customer uses the form.
 */
function bindGroup(group, answers, spans, tokens) {
    const answered = group.filter((field) => answers[field.key] !== undefined);
    const fieldKeys = answered.map((field) => field.key);
    for (const field of answered) {
        if (!spans.some((span) => span.value === answers[field.key])) {
            return { reason: "NOT_IN_MESSAGE", fieldKeys: [field.key] };
        }
    }
    if (spans.length > group.length)
        return { reason: "AMBIGUOUS", fieldKeys };
    const cues = new Map(group.map((field) => [field.key, cueRuns(field, group)]));
    const cued = (field, span, side) => (cues.get(field.key) ?? []).some((run) => side === "after"
        ? runAt(tokens, span.end + 1, run)
        : runAt(tokens, span.start - run.length, run));
    const bindAll = (side) => {
        const used = new Set();
        const picked = [];
        for (const field of answered) {
            const matches = spans.flatMap((span, index) => span.value === answers[field.key] &&
                cued(field, span, side) &&
                !group.some((other) => other !== field && cued(other, span, side))
                ? [index]
                : []);
            const only = matches[0];
            if (matches.length !== 1 || only === undefined || used.has(only))
                return null;
            used.add(only);
            picked.push(only);
        }
        return picked;
    };
    const after = bindAll("after");
    const before = bindAll("before");
    if (after === null && before === null)
        return { reason: "AMBIGUOUS", fieldKeys };
    if (after !== null && before !== null && after.some((index, at) => index !== before[at])) {
        return { reason: "AMBIGUOUS", fieldKeys };
    }
    return null;
}
/**
 * Decides whether the agent may record `proposed` as the answer of `fields`, from ONE customer message (contract 7.4). The server calls
 * it with the message it read itself; `proposed` is what the model said the message holds. All checks must pass for EVERY field, else the
 * verdict is `CLARIFY` and nothing is recorded (the agent asks the customer to use the form). The rules:
 *
 *   1. The author is the customer, and the message was sent after the question was asked (`sentAt` strictly after `askedAt`).
 *   2. The values are valid for the fields (types, required, options) and no key is unknown.
 *   3. A TEXT answer is the COMPLETE reply: after normalization (NFKC, lower case, one space, surrounding punctuation stripped) it equals
 *      the whole message, never a selected fragment. Every other value is a WHOLE token, or a whole sequence of tokens, of the message:
 *      `2` is not in `12`, `no` is not in `not now`.
 *   4. A message with a doubt in it answers `CLARIFY` for every field that is not a whole-message TEXT: a negation (`not`, `no`,
 *      `don't`), a contrast (`but`), a condition (`if`, `unless`), a hedge (`maybe`), a choice between values (`or`), a quotation mark or
 *      a question mark. "Not 2 guests" is not 2 guests. The one exception: a message that is only `yes` or `no` answers a BOOLEAN.
 *   5. A NUMBER is a plain unsigned integer or decimal. A sign (`-2`, `+2`), a range (`2-3`), a fraction, a thousands separator or a
 *      digit of another script anywhere in a message that is read for a NUMBER field is a `CLARIFY`. A DATE is an explicit date
 *      (`2026-11-03`, `3 November 2026`): `tomorrow` is `CLARIFY`. A TIME is an explicit time (`14:30`, `2pm`, `02:30 pm` is 14:30).
 *   6. A BOOLEAN needs the whole word `yes` or `no`. A CHOICE needs the whole option text and only one option. A question with two
 *      BOOLEAN fields, or two TEXT fields, cannot be answered from one message.
 *   7. A message that holds more numbers, dates or times than the question has fields of that kind is ambiguous: `12 guests at 2` could
 *      be a party of 2. When the question has several fields of one type, each value must stand next to the label (or key words) of its
 *      field, each value token for one field only (`4 adults and 2 children`, never `{adults: 2, children: 4}`).
 */
export function answerFromChatMessage(input) {
    const { fields, message } = input;
    if (message.author !== "customer")
        return clarify("NOT_CUSTOMER_MESSAGE");
    if (message.sentAt === null || Number.isNaN(message.sentAt.getTime())) {
        return clarify("MESSAGE_TIME_UNKNOWN");
    }
    if (message.sentAt.getTime() <= input.askedAt.getTime())
        return clarify("BEFORE_QUESTION");
    if (!isRecord(input.proposed))
        return clarify("VALUE_INVALID");
    const keys = fields.map((field) => field.key);
    const unknownKeys = Object.keys(input.proposed).filter((key) => !keys.includes(key));
    if (unknownKeys.length > 0)
        return clarify("UNKNOWN_FIELD", unknownKeys);
    // Types, required fields and options first: a wrong type is a doubt, not something to repair.
    let answers;
    try {
        answers = validateQuestionAnswer(fields, input.proposed);
    }
    catch (error) {
        if (!(error instanceof DomainValidationError))
            throw error;
        const key = error.field.startsWith("answers.") ? error.field.slice("answers.".length) : null;
        const missing = /is required/.test(error.message);
        return clarify(missing ? "FIELD_MISSING" : "VALUE_INVALID", key === null ? [] : [key]);
    }
    const tokens = chatTokens(message.text);
    const dateSpans = dateSpansIn(tokens);
    const timeSpans = timeSpansIn(tokens);
    const covered = new Set();
    for (const span of [...dateSpans, ...timeSpans]) {
        for (let at = span.start; at <= span.end; at += 1)
            covered.add(at);
    }
    // The day and the year of a written date and the hour of `7 pm` are not plain numbers: a NUMBER field cannot take them.
    const isPartOfDateOrTime = (index) => covered.has(index) ||
        MONTHS[tokens[index - 1] ?? ""] !== undefined ||
        MONTHS[tokens[index + 1] ?? ""] !== undefined ||
        MERIDIEM[tokens[index + 1] ?? ""] !== undefined;
    const numberSpans = [];
    let unsupportedNumber = false;
    tokens.forEach((token, index) => {
        if (isPartOfDateOrTime(index))
            return;
        if (PLAIN_NUMBER.test(token))
            numberSpans.push({ value: Number(token), start: index, end: index });
        else if (NUMERICISH.test(token))
            unsupportedNumber = true;
    });
    const relative = tokens.some((token) => RELATIVE_WORDS.has(token));
    const group = (type) => fields.filter((field) => field.type === type);
    // More explicit dates, times or plain numbers in the message than the question has fields of that kind: which is which is a guess.
    // A kind the question does not ask for is not judged (a note may hold any number).
    const tooMany = (type, found) => group(type).length > 0 && found > group(type).length;
    if (tooMany("DATE", new Set(dateSpans.map((span) => span.value)).size))
        return clarify("AMBIGUOUS");
    if (tooMany("TIME", new Set(timeSpans.map((span) => span.value)).size))
        return clarify("AMBIGUOUS");
    if (tooMany("NUMBER", new Set(numberSpans.map((span) => span.value)).size))
        return clarify("AMBIGUOUS");
    for (const type of ["BOOLEAN", "TEXT"]) {
        const same = group(type);
        if (same.length > 1)
            return clarify("USE_FORM", same.map((field) => field.key));
    }
    const choices = group("CHOICE");
    if (choices.length > 1) {
        for (const field of choices) {
            const value = answers[field.key];
            if (value === undefined)
                continue;
            const shared = choices.some((other) => other !== field &&
                (other.options ?? []).some((option) => chatTokens(option).join(" ") === chatTokens(String(value)).join(" ")));
            if (shared)
                return clarify("AMBIGUOUS", [field.key]);
        }
    }
    const spansOf = {
        NUMBER: numberSpans,
        DATE: dateSpans,
        TIME: timeSpans,
    };
    for (const type of ["NUMBER", "DATE", "TIME"]) {
        if (group(type).length < 2)
            continue;
        const refusal = bindGroup(group(type), answers, spansOf[type], tokens);
        if (refusal !== null)
            return clarify(refusal.reason, refusal.fieldKeys);
    }
    const facts = {
        tokens,
        dates: dateSpans.map((span) => span.value),
        times: timeSpans.map((span) => span.value),
        numbers: numberSpans.map((span) => span.value),
        relative,
        unsupportedNumber,
        doubtMark: DOUBT_MARKS.test(message.text.normalize("NFKC")),
        doubts: doubtIndexes(tokens),
    };
    for (const field of fields) {
        const value = answers[field.key];
        if (value === undefined)
            continue; // an optional field that was left out
        const refusal = checkValue(field, value, facts);
        if (refusal !== null)
            return clarify(refusal, [field.key]);
    }
    return { outcome: "ACCEPTED", answers };
}
function checkValue(field, value, facts) {
    if (field.type === "TEXT") {
        // The complete reply, never a selected fragment: a doubt word in it is the customer's own text, so it does not block.
        const needle = chatTokens(String(value));
        const whole = needle.length === facts.tokens.length &&
            needle.every((token, at) => token === facts.tokens[at]);
        return whole ? null : "NOT_IN_MESSAGE";
    }
    // Any other value read from a message with a doubt in it may have lost its meaning ("not 2 guests"). A message that is only `yes` or
    // `no` is the one plain answer of a BOOLEAN.
    if (facts.doubtMark)
        return "AMBIGUOUS";
    const bareAnswer = field.type === "BOOLEAN" && facts.tokens.length === 1;
    let doubts = bareAnswer ? [] : facts.doubts;
    switch (field.type) {
        case "NUMBER": {
            if (doubts.length > 0)
                return "AMBIGUOUS";
            if (facts.unsupportedNumber)
                return "VALUE_INVALID";
            const number = value;
            if (number < 0)
                return "VALUE_INVALID";
            return facts.numbers.includes(number) ? null : "NOT_IN_MESSAGE";
        }
        case "DATE":
            if (doubts.length > 0)
                return "AMBIGUOUS";
            if (facts.dates.includes(value))
                return null;
            return facts.relative ? "RELATIVE_DATE" : "NOT_IN_MESSAGE";
        case "TIME":
            if (doubts.length > 0)
                return "AMBIGUOUS";
            return facts.times.includes(value) ? null : "NOT_IN_MESSAGE";
        case "CHOICE": {
            const options = field.options ?? [];
            const needle = chatTokens(String(value));
            const start = findRun(facts.tokens, needle);
            if (start === -1)
                return "NOT_IN_MESSAGE";
            // A doubt word that is part of the chosen option itself ("No preference") is not a doubt.
            doubts = doubts.filter((index) => index < start || index >= start + needle.length);
            if (doubts.length > 0)
                return "AMBIGUOUS";
            // The message names another option too: the customer may have listed both.
            const others = options.filter((option) => option !== value);
            const namesOther = others.some((option) => {
                const run = chatTokens(option);
                return run.length > 0 && countRuns(facts.tokens, run) > 0 && findRun(needle, run) === -1;
            });
            return namesOther ? "AMBIGUOUS" : null;
        }
        case "BOOLEAN": {
            if (doubts.length > 0)
                return "AMBIGUOUS";
            const yes = facts.tokens.indexOf("yes");
            const no = facts.tokens.indexOf("no");
            if (yes === -1 && no === -1)
                return "NOT_IN_MESSAGE";
            if (yes !== -1 && no !== -1)
                return "AMBIGUOUS";
            return (yes !== -1) === value ? null : "NOT_IN_MESSAGE";
        }
    }
}
